/**
 * Hero sample B — outlined anatomy + pulsing data nodes.
 *
 * Pure inline SVG. Hand-drawn human silhouette with anatomical highlight
 * dots (shoulder, lumbar, knee, ankle) pulsing in sequence. Lines connect
 * them to a floating "PATIENT CHART" card on the right.
 */
import React from 'react';

export default function HeroSampleB() {
  return (
    <svg viewBox="0 0 600 600" className="w-full h-full" data-testid="hero-sample-b" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="b-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#F2A100" stopOpacity="0.45"/>
          <stop offset="100%" stopColor="#F2A100" stopOpacity="0"/>
        </radialGradient>
        <linearGradient id="b-chart" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F2EBDB"/>
          <stop offset="100%" stopColor="#fff"/>
        </linearGradient>
      </defs>
      {/* Backdrop halo */}
      <circle cx="220" cy="290" r="240" fill="url(#b-glow)"/>
      {/* Stylised human silhouette (front view) */}
      <g stroke="#F2EBDB" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
        {/* Head */}
        <circle cx="220" cy="125" r="38"/>
        {/* Neck */}
        <line x1="220" y1="163" x2="220" y2="185"/>
        {/* Shoulders */}
        <path d="M150 200 Q220 175 290 200"/>
        {/* Torso */}
        <path d="M160 200 L175 380 L220 405 L265 380 L280 200"/>
        {/* Lumbar line */}
        <line x1="180" y1="310" x2="260" y2="310" strokeDasharray="4 4" opacity="0.6"/>
        {/* Arms */}
        <path d="M150 200 L120 325 L130 360"/>
        <path d="M290 200 L320 325 L310 360"/>
        {/* Legs */}
        <path d="M195 405 L185 555"/>
        <path d="M245 405 L255 555"/>
      </g>
      {/* Pulsing data nodes — each with a delayed CSS animation */}
      {[
        { cx: 200, cy: 200, label: 'Shoulder', delay: 0 },
        { cx: 220, cy: 310, label: 'Lumbar',   delay: 0.6 },
        { cx: 200, cy: 470, label: 'Knee',     delay: 1.2 },
        { cx: 250, cy: 540, label: 'Ankle',    delay: 1.8 },
      ].map((n, i) => (
        <g key={i}>
          <circle cx={n.cx} cy={n.cy} r="14" fill="#F2A100" opacity="0.25">
            <animate attributeName="r" values="10;22;10" dur="2.4s" begin={`${n.delay}s`} repeatCount="indefinite"/>
            <animate attributeName="opacity" values="0.45;0.05;0.45" dur="2.4s" begin={`${n.delay}s`} repeatCount="indefinite"/>
          </circle>
          <circle cx={n.cx} cy={n.cy} r="6" fill="#F2A100"/>
          {/* Connector to chart */}
          <line x1={n.cx + 6} y1={n.cy} x2="380" y2={n.cy} stroke="#F2A100" strokeWidth="1" strokeDasharray="3 4" opacity="0.55"/>
        </g>
      ))}
      {/* Floating patient chart card */}
      <g transform="translate(380 130)">
        <rect width="180" height="420" rx="16" fill="url(#b-chart)" stroke="#003D80" strokeWidth="1.5"/>
        <rect x="0" y="0" width="180" height="46" rx="16" fill="#003D80"/>
        <text x="14" y="29" fill="#fff" fontSize="13" fontFamily="ui-monospace, monospace" fontWeight="700">PATIENT CHART</text>
        {/* Section headers */}
        <text x="14" y="80"  fill="#003D80" fontSize="10" fontWeight="700" fontFamily="ui-sans-serif, sans-serif">SUBJECTIVE</text>
        <line x1="14" y1="88" x2="80" y2="88" stroke="#F2A100" strokeWidth="2"/>
        {[100, 114, 128, 142].map((y, i) => (
          <line key={i} x1="14" y1={y} x2={i % 2 ? 150 : 160} y2={y} stroke="#003D80" strokeWidth="1" opacity="0.45"/>
        ))}
        <text x="14" y="180" fill="#003D80" fontSize="10" fontWeight="700">RANGE OF MOTION</text>
        <line x1="14" y1="188" x2="100" y2="188" stroke="#F2A100" strokeWidth="2"/>
        {/* ROM "table" */}
        <g fontFamily="ui-monospace, monospace" fontSize="9" fill="#003D80">
          <text x="14" y="210">Flex</text><text x="120" y="210" textAnchor="end">90°</text>
          <text x="14" y="226">Ext</text> <text x="120" y="226" textAnchor="end">30°</text>
          <text x="14" y="242">Abd</text> <text x="120" y="242" textAnchor="end">120°</text>
          <text x="14" y="258">IR</text>  <text x="120" y="258" textAnchor="end">45°</text>
        </g>
        <text x="14" y="290" fill="#003D80" fontSize="10" fontWeight="700">TREATMENT PLAN</text>
        <line x1="14" y1="298" x2="110" y2="298" stroke="#F2A100" strokeWidth="2"/>
        {[310, 324, 338].map((y, i) => (
          <line key={i} x1="14" y1={y} x2={140 - i * 18} y2={y} stroke="#003D80" strokeWidth="1" opacity="0.45"/>
        ))}
        {/* Footer chip */}
        <rect x="14" y="370" width="60" height="22" rx="11" fill="#F2A100"/>
        <text x="44" y="385" textAnchor="middle" fontSize="9" fontFamily="ui-sans-serif, sans-serif" fontWeight="700" fill="#003D80">AI ASSISTED</text>
      </g>
      {/* Tiny floating brand mark */}
      <g transform="translate(40 540)" opacity="0.6">
        <circle r="6" fill="#F2A100"/>
        <text x="14" y="4" fill="#F2EBDB" fontSize="11" fontFamily="ui-sans-serif" fontWeight="700">PHYSIOGENIC</text>
      </g>
    </svg>
  );
}
